import React from "react";
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import analyticsApi from "../api/analyticsApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";

const DashboardPage = () => {
  const [showGuide, setShowGuide] = React.useState(false);
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadAnalytics = can(PERFORMANCE_PERMISSIONS.ANALYTICS_READ);

  if (!canReadAnalytics) {
    return (
      <ModulePageShell
        title="Performance Dashboard"
        description="KPI snapshot and trend analytics across goals, reviews, and feedback."
      >
        <EmptyState message="You do not have permission to view performance analytics." />
      </ModulePageShell>
    );
  }

  const overviewQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.dashboard, "overview"],
    queryFn: () => analyticsApi.getOverview(),
  });
  const goalCompletionQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.dashboard, "goalCompletion"],
    queryFn: () => analyticsApi.getGoalCompletion(),
  });
  const reviewCompletionQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.dashboard, "reviewCompletion"],
    queryFn: () => analyticsApi.getReviewCompletion(),
  });
  const feedbackTrendQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.dashboard, "feedbackTrends"],
    queryFn: () => analyticsApi.getFeedbackTrends(),
  });

  const loading =
    overviewQuery.isLoading ||
    goalCompletionQuery.isLoading ||
    reviewCompletionQuery.isLoading ||
    feedbackTrendQuery.isLoading;

  const overview = overviewQuery.data?.data || {};
  const goalCompletion = goalCompletionQuery.data?.data || [];
  const reviewCompletion = reviewCompletionQuery.data?.data || [];
  const feedbackTrends = feedbackTrendQuery.data?.data || [];

  const kpis = [
    { label: "Total Goals", value: overview.totalGoals || 0 },
    { label: "Goals Completed", value: overview.completedGoals || 0 },
    { label: "Feedback Received", value: overview.feedbackCount || 0 },
    { label: "Reviews Pending", value: overview.pendingReviews || 0 },
    { label: "Active Campaigns", value: overview.activeCampaigns || 0 },
    {
      label: "Competencies Assessed",
      value: overview.competenciesAssessed || 0,
    },
  ];

  return (
    <ModulePageShell
      title="Performance Dashboard"
      description="KPI snapshot and trend analytics across goals, reviews, and feedback."
      onRefresh={() => {
        overviewQuery.refetch();
        goalCompletionQuery.refetch();
        reviewCompletionQuery.refetch();
        feedbackTrendQuery.refetch();
      }}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {kpis.map((item) => (
              <Box
                key={item.label}
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <Stat>
                  <StatLabel>{item.label}</StatLabel>
                  <StatNumber>{item.value}</StatNumber>
                </Stat>
              </Box>
            ))}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mt={4}>
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Goal Completion %
              </Text>
              <Box h="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Array.isArray(goalCompletion) ? goalCompletion : []}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#3182ce" name="Completion" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Review Completion
              </Text>
              <Box h="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      Array.isArray(reviewCompletion) ? reviewCompletion : []
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#38a169" name="Completion" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </SimpleGrid>

          <Box
            mt={4}
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="semibold" mb={3}>
              Feedback Trends
            </Text>
            <Box h="280px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={Array.isArray(feedbackTrends) ? feedbackTrends : []}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#805ad5"
                    strokeWidth={2}
                    name="Feedback"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </>
      )}
    </ModulePageShell>
  );
};

export default DashboardPage;
